import { AnyAction, AsyncThunk, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AppDispatch } from '@nextgen-web-framework/core';
import { cloneDeep } from 'lodash';
import type { InitialCartListState, ApiProjections } from '../../types';
import type { ErrorResponseSubDetailsInner } from '../models';
import { ListResponse, ListType, UserListResponse } from '../list';
import { myListApi } from '../requests';
import { CartAPIResponse } from '../../types/cartTypes';

type GenericAsyncThunk = AsyncThunk<
  CartAPIResponse,
  unknown,
  {
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialCartListState; // this is
  }
>;
type PendingAction = ReturnType<GenericAsyncThunk['pending']>;
type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;
type FulfilledAction = ReturnType<GenericAsyncThunk['fulfilled']>;

function isPendingGenericAction(action: AnyAction): action is PendingAction {
  return action.type.startsWith('commerceMyList') && action.type.endsWith('/pending');
}
function isFulfilledAction(action: AnyAction): action is FulfilledAction {
  return action.type.startsWith('commerceMyList') && action.type.endsWith('/fulfilled');
}

function isRejectedAction(action: AnyAction): action is RejectedAction {
  return action.type.startsWith('commerceMyList') && action.type.endsWith('/rejected');
}
const sliceReqName = `commerceMyList`;
const getMyList = `getMyList`;

export const getMyListsByTypeSliceReq = createAsyncThunk<
  ListResponse,
  {
    type: ListType;
    projections: ApiProjections;
  }
>(`${sliceReqName}/${getMyList}`, (payload) => myListApi.getListsByType(payload) as ListResponse);

const initialState: InitialCartListState = {
  myList: {},
  listsByUser: {},
  isLoading: true,
  isSponserBlocked: false,
  hasError: false,
  error: {},
};

export const myListSlice = createSlice({
  name: 'commerceMyList',
  initialState,
  reducers: {
    resetMyListSlice: () => initialState,
    updateMyList: (state, action: PayloadAction<ListResponse>) => {
      state.myList = action.payload;
    },
    updateListsByUser: (state, action: PayloadAction<UserListResponse>) => {
      state.listsByUser = action.payload;
    },
    updateSpecificListByUser: (state, action: PayloadAction<UserListResponse>) => {
      const listToBeUpdated: Record<string, ListResponse> = {};
      action.payload?.lists?.forEach((list) => {
        listToBeUpdated[String(list.listId)] = list;
      });
      const allList = cloneDeep(state.listsByUser);
      allList.lists = allList?.lists?.map((list) => {
        if (listToBeUpdated[String(list.listId)]) {
          return listToBeUpdated[String(list.listId)];
        }

        return list;
      });
      state.listsByUser = allList;
    },
    updateIsSponserBlocked: (state, action: PayloadAction<boolean>) => {
      state.isSponserBlocked = action.payload;
    },
  },
  extraReducers: (builder) => {
    /**
     * Common reducers
     */
    builder.addMatcher(isRejectedAction, (state: InitialCartListState, action: PayloadAction<unknown>) => {
      state.hasError = true;
      state.isLoading = false;
      state.error = action as ErrorResponseSubDetailsInner;
    });
    builder.addMatcher(isFulfilledAction, (state: InitialCartListState, action: PayloadAction<unknown>) => {
      state.hasError = false;
      state.myList = action.payload as ListResponse;
    });
    builder.addMatcher(isPendingGenericAction, (state: InitialCartListState) => {
      state.hasError = false;
    });
  },
});

export const { updateMyList, resetMyListSlice, updateListsByUser, updateSpecificListByUser, updateIsSponserBlocked } =
  myListSlice.actions;
